// Analytics helper for Google Analytics, Facebook Pixel, and Backend tracking

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Track to backend
const trackToBackend = async (eventType, eventData = {}) => {
  try {
    // Read user_id from the main auth storage (mintedUser JSON)
    let userId = null;
    try {
      const storedUser = localStorage.getItem('mintedUser');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        userId = parsed?.id || null;
      }
    } catch {}
    await fetch(`${API_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: eventType,
        user_id: userId,
        data: eventData,
        timestamp: new Date().toISOString()
      })
    });
  } catch (error) {
    console.error('Analytics tracking error:', error);
  }
};

// Track page views
export const trackPageView = (pagePath, pageTitle) => {
  // Backend tracking
  trackToBackend('page_view', { page_path: pagePath, page_title: pageTitle });
  
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }
  // Facebook Pixel
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }
};

// Track product view
export const trackProductView = (product) => {
  if (!product) return;
  
  // Backend tracking
  trackToBackend('product_view', { 
    product_id: product.id, 
    product_title: product.title,
    category: product.category 
  });
  
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'view_item', {
      currency: 'AUD',
      value: product.price,
      items: [{
        item_id: product.id,
        item_name: product.title,
        item_category: product.category,
        price: product.price,
      }],
    });
  }
  // Facebook Pixel
  if (window.fbq) {
    window.fbq('track', 'ViewContent', {
      content_name: product.title,
      content_ids: [product.id],
      content_type: 'product',
      value: product.price,
      currency: 'AUD',
    });
  }
};

// Track add to cart / initiate checkout
export const trackInitiateCheckout = (product) => {
  if (!product) return;
  
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'begin_checkout', {
      currency: 'AUD',
      value: product.price,
      items: [{
        item_id: product.id,
        item_name: product.title,
        item_category: product.category,
        price: product.price,
      }],
    });
  }
  // Facebook Pixel
  if (window.fbq) {
    window.fbq('track', 'InitiateCheckout', {
      content_name: product.title,
      content_ids: [product.id],
      content_type: 'product',
      value: product.price,
      currency: 'AUD',
    });
  }
};

// Track purchase
export const trackPurchase = (product, transactionId) => {
  if (!product) return;
  
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      currency: 'AUD',
      value: product.price,
      items: [{
        item_id: product.id,
        item_name: product.title,
        item_category: product.category,
        price: product.price,
      }],
    });
  }
  // Facebook Pixel
  if (window.fbq) {
    window.fbq('track', 'Purchase', {
      content_name: product.title,
      content_ids: [product.id],
      content_type: 'product',
      value: product.price,
      currency: 'AUD',
    });
  }
};

// Track sign up
export const trackSignUp = (method = 'email') => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'sign_up', { method });
  }
  // Facebook Pixel
  if (window.fbq) {
    window.fbq('track', 'CompleteRegistration');
  }
};

// Track listing created
export const trackListingCreated = (product) => {
  if (!product) return;
  
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'create_listing', {
      currency: 'AUD',
      value: product.price,
      item_category: product.category,
    });
  }
  // Facebook Pixel - custom event
  if (window.fbq) {
    window.fbq('trackCustom', 'ListingCreated', {
      content_name: product.title,
      value: product.price,
      currency: 'AUD',
    });
  }
};

// Track search
export const trackSearch = (searchTerm) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'search', { search_term: searchTerm });
  }
  // Facebook Pixel
  if (window.fbq) {
    window.fbq('track', 'Search', { search_string: searchTerm });
  }
};

// Track share
export const trackShare = (product, method) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'share', {
      method: method,
      content_type: 'product',
      item_id: product?.id,
    });
  }
  // Facebook Pixel
  if (window.fbq) {
    window.fbq('track', 'Share', {
      content_name: product?.title,
      method: method,
    });
  }
};

export default {
  trackPageView,
  trackProductView,
  trackInitiateCheckout,
  trackPurchase,
  trackSignUp,
  trackListingCreated,
  trackSearch,
  trackShare,
};
